import { useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

/**
 * Restarts the backend process (sends itself SIGTERM after a short delay —
 * Docker's `restart: unless-stopped` policy then brings up a fresh
 * container). Briefly takes the whole application down for every user, so
 * this is gated to System Administrator and requires an explicit confirm.
 */
export function RestartApplicationCard() {
  const { user } = useAuth();
  const isSystemAdmin = hasGlobalPermission(user, PERM.SYSTEM_RESTART);
  const [restarting, setRestarting] = useState(false);

  const restart = async () => {
    if (!window.confirm("Restart the application now? Every user will briefly lose their connection.")) {
      return;
    }
    setRestarting(true);
    try {
      await client.post("/admin/system/restart");
      toast.success("Restarting — the application will be back in a few seconds.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to restart");
      setRestarting(false);
    }
  };

  return (
    <Card className={`p-6 ${!isSystemAdmin ? "opacity-60" : ""}`} data-testid="settings-restart-app-card">
      <div className="mb-1 flex items-center gap-2">
        <RotateCcw className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Restart Application</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Restarts the backend process. Briefly takes the application down for every user — use this
        after a config change that needs a fresh process, or if the app seems stuck.
        {!isSystemAdmin && (
          <span className="ml-2 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
            System Administrator only
          </span>
        )}
      </p>
      <Button
        data-testid="settings-restart-app-btn"
        variant="destructive"
        onClick={restart}
        disabled={!isSystemAdmin || restarting}
        className="gap-1.5"
      >
        <RotateCcw className="h-3.5 w-3.5" />
        {restarting ? "Restarting…" : "Restart Application"}
      </Button>
    </Card>
  );
}
